import { useEffect, useState } from 'react';
import { ReactFlow, Background, Controls } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import api from '../../services/api';
import { RectangleNode, CircleNode, EllipseNode, DiamondNode, ParallelogramNode } from './CustomNodes';
import '../../styles/reactflow-overrides.css';

interface FlowChartEmbedProps {
  slug: string;
}

const nodeTypes = {
  rectangle: RectangleNode,
  circle: CircleNode,
  ellipse: EllipseNode,
  diamond: DiamondNode,
  parallelogram: ParallelogramNode,
  default: RectangleNode,
};

export default function FlowChartEmbed({ slug }: FlowChartEmbedProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nodes, setNodes] = useState<any[]>([]);
  const [edges, setEdges] = useState<any[]>([]);

  useEffect(() => {
    async function fetchFlowchart() {
      try {
        setLoading(true);
        const response = await api.get(`/flowcharts/${slug}/embed`);
        const { content } = response.data;
        
        if (content) {
          // Transform nodes to React Flow format
          const flowNodes = content.nodes.map((node: any) => ({
            id: node.id,
            type: node.type || 'default',
            position: node.position,
            data: {
              label: node.data?.label || 'Node',
              style: node.data?.style || node.style,
              shape: node.data?.shape || node.type,
              connections: node.data?.connections || 'all'
            }
          }));
          
          // Transform edges to React Flow format
          const flowEdges = content.edges.map((edge: any) => {
            console.log('Embed loading edge:', edge);
            return {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              label: edge.label,
              style: {
                stroke: edge.style?.stroke || edge.data?.style?.stroke || '#9333ea',
                strokeWidth: edge.style?.strokeWidth || edge.data?.style?.strokeWidth || 2,
                strokeDasharray: edge.style?.strokeDasharray || edge.data?.style?.strokeDasharray || '0'
              }
            };
          });
          console.log('Embed loaded edges:', flowEdges);
          
          setNodes(flowNodes);
          setEdges(flowEdges);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load flowchart');
      } finally {
        setLoading(false);
      }
    }

    fetchFlowchart();
  }, [slug]);

  if (loading) {
    return (
      <div style={{ 
        width: '100%', 
        height: '400px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ color: '#6b7280' }}>Loading flowchart...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        width: '100%', 
        minHeight: '200px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        border: '1px solid #fee2e2',
        borderRadius: '8px',
        backgroundColor: '#fef2f2'
      }}>
        <div style={{ color: '#dc2626' }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      height: '500px', 
      border: '1px solid var(--accent-alpha-15)',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: 'var(--bg-primary)'
    }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes as any}
        nodesDraggable={false}
        nodesConnectable={false}
        panOnDrag={true}
        panOnScroll={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        fitView
        attributionPosition="bottom-left"
      >
        <Background color="var(--accent-alpha-15)" gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
